import { t as translate } from "./preferences";
import { api, ApiError, type WarehouseOperationKind, type WarehouseWarningOut } from "./api";

export const WAREHOUSE_OPERATION_PREFIX = "avtodaftar_mechanic_warehouse_operation_v1:";

export type PendingWarehouseOperation = {
  version: 1;
  userId: number;
  workshopId: number;
  kind: WarehouseOperationKind;
  operationUuid: string;
  payload: Record<string, unknown>;
  resourceId?: number;
  createdAt: number;
  replayUsed: boolean;
  outcomeUnknown: boolean;
};

export type WarehouseOperationResult =
  | { status: "success"; result: WarehouseWarningOut }
  | { status: "unknown"; operation: PendingWarehouseOperation }
  | { status: "error"; error: unknown };

export function warehouseOperationKey(userId: number): string {
  return `${WAREHOUSE_OPERATION_PREFIX}${userId}`;
}

export function readWarehouseOperation(userId: number, workshopId: number): PendingWarehouseOperation | null {
  try {
    const raw = window.localStorage.getItem(warehouseOperationKey(userId));
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingWarehouseOperation;
    if (
      value.version !== 1 || value.userId !== userId || value.workshopId !== workshopId ||
      !value.kind || !value.operationUuid || !value.payload || !Number.isFinite(value.createdAt)
    ) return null;
    return value;
  } catch {
    return null;
  }
}

export function writeWarehouseOperation(operation: PendingWarehouseOperation): boolean {
  try {
    window.localStorage.setItem(warehouseOperationKey(operation.userId), JSON.stringify(operation));
    return true;
  } catch {
    return false;
  }
}

export function clearWarehouseOperation(userId: number, operationUuid?: string): void {
  try {
    const raw = window.localStorage.getItem(warehouseOperationKey(userId));
    if (operationUuid && raw && JSON.parse(raw).operationUuid !== operationUuid) return;
    window.localStorage.removeItem(warehouseOperationKey(userId));
  } catch { /* unavailable */ }
}

/** Logout may clear editable UI, but an outcome-unknown mutation is never discarded. */
export function clearWarehouseOperationUnlessPending(userId: number): boolean {
  try {
    const raw = window.localStorage.getItem(warehouseOperationKey(userId));
    if (raw) {
      const value = JSON.parse(raw) as Partial<PendingWarehouseOperation>;
      if (value.version === 1 && value.userId === userId && value.operationUuid && value.payload) return false;
    }
    window.localStorage.removeItem(warehouseOperationKey(userId));
    return true;
  } catch {
    return false;
  }
}

export function isAmbiguousWarehouseError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status === 408 || error.status >= 500);
}

export function warehouseReconciliationMustContinue(error: unknown, outcomeUnknown: boolean): boolean {
  if (isAmbiguousWarehouseError(error)) return true;
  return outcomeUnknown && error instanceof ApiError && [401, 402, 403].includes(error.status);
}

async function send(operation: PendingWarehouseOperation): Promise<WarehouseWarningOut> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    return await sendWithSignal(operation, controller.signal);
  } finally {
    window.clearTimeout(timeout);
  }
}

async function sendWithSignal(operation: PendingWarehouseOperation, signal: AbortSignal): Promise<WarehouseWarningOut> {
  switch (operation.kind) {
    case "purchase": return api.createPurchase(operation.payload, signal);
    case "sale": return api.createSale(operation.payload, signal);
    case "purchase-return": return api.createPurchaseReturn(operation.payload, signal);
    case "sale-return": return api.createSaleReturn(operation.payload, signal);
    case "purchase-document-return":
    case "sale-document-return": return api.createLinkedReturn(operation.payload, signal);
    case "write-off": return api.createWriteOff(operation.payload, signal);
    case "jarayonda-reserve": return api.createJarayonda(operation.payload, signal);
    case "jarayonda-confirm":
      if (!operation.resourceId) throw new ApiError("jarayonda_not_found", translate("Jarayondagi sotuv topilmadi"), 404);
      return api.confirmJarayonda(operation.resourceId, operation.payload, signal);
  }
}

async function reconcileOrSend(operation: PendingWarehouseOperation): Promise<WarehouseWarningOut> {
  if (operation.kind !== "jarayonda-confirm" || !operation.resourceId) return send(operation);
  const state = await api.jarayondaState(operation.resourceId);
  if (state.status === "active") return { id: state.id };
  if (state.status !== "jarayonda") {
    throw new ApiError("jarayonda_not_open", translate("Jarayondagi sotuv allaqachon yopilgan"), 409);
  }
  return send(operation);
}

/**
 * One immediate idempotent replay is allowed after an ambiguous response. Once
 * an outcome is unknown every later attempt keeps the same identity, including
 * auth/session failures, until the server gives a conclusive answer.
 */
export async function settleWarehouseOperation(
  original: PendingWarehouseOperation,
  options: { allowReplay: boolean; resume?: boolean },
): Promise<WarehouseOperationResult> {
  const startingUnknown = Boolean(original.outcomeUnknown || options.resume);
  try {
    const result = startingUnknown ? await reconcileOrSend(original) : await send(original);
    clearWarehouseOperation(original.userId, original.operationUuid);
    return { status: "success", result };
  } catch (error) {
    if (isAmbiguousWarehouseError(error) && options.allowReplay && !original.replayUsed) {
      const replay = { ...original, replayUsed: true, outcomeUnknown: true };
      writeWarehouseOperation(replay);
      try {
        const result = await reconcileOrSend(replay);
        clearWarehouseOperation(original.userId, original.operationUuid);
        return { status: "success", result };
      } catch (replayError) {
        if (warehouseReconciliationMustContinue(replayError, true)) {
          return { status: "unknown", operation: replay };
        }
        clearWarehouseOperation(original.userId, original.operationUuid);
        return { status: "error", error: replayError };
      }
    }
    if (warehouseReconciliationMustContinue(error, startingUnknown)) {
      const pending = { ...original, outcomeUnknown: true };
      writeWarehouseOperation(pending);
      return { status: "unknown", operation: pending };
    }
    clearWarehouseOperation(original.userId, original.operationUuid);
    return { status: "error", error };
  }
}
